import Image from 'next/image';
import React, { ReactElement } from 'react';

import { WHITE_PAPER_LINK } from '../../../../../globals';
import { useGetAppOrSignUpHandler } from '../../../../../hooks/useGetAppOrSignUpHandler';
import { Assets } from '../../../../../utils/assets';
import { PrimaryButtonLink } from '../../../../button';
import { SCIENCE_EXTERNAL_LINK } from '../../constants';
import * as S from './Content.styles';

export const Content = (): ReactElement => {
  const handleClickCta = useGetAppOrSignUpHandler();
  return (
    <S.Wrapper>
      <S.CircleBlur>
        <Image
          alt={Assets.images.circleOfBlurs.alt}
          fill
          loading="eager"
          priority
          src={Assets.images.circleOfBlurs.url}
        />
      </S.CircleBlur>
      <S.PanelWrapper>
        <S.Section>
          <S.Content>
            <S.SectionTextContainer>
              <S.SubCaption>How do we know Brain.fm works?</S.SubCaption>
              <S.Caption>Performance testing with placebo controls</S.Caption>
              <S.Text>
                We test our music with simple ‘games’ used to measure focused attention in
                psychology research. Our experiments always include a control (‘placebo’) of the
                same music without Brain.fm technology, so we can be sure our tech is what makes the
                difference.
              </S.Text>
              <S.CtaWrapper>
                <PrimaryButtonLink
                  analyticsEvent={{
                    category: 'Purple CTA',
                    action: 'Science Page: CTA Button Click',
                  }}
                  href={SCIENCE_EXTERNAL_LINK}
                  isAnchortag={true}
                  rel="nofollow"
                  target="_blank"
                  variant="dark"
                >
                  Read the latest science
                </PrimaryButtonLink>
              </S.CtaWrapper>
            </S.SectionTextContainer>
            <S.ImageWrapper>
              <Image
                alt={Assets.images.sciencePerformance.alt}
                height={492}
                src={Assets.images.sciencePerformance.url}
                style={{ height: 'auto', width: '100%' }}
                width={1470}
              />
            </S.ImageWrapper>
            <S.SpacedWrapper>
              <S.SpacedTextWrapper>
                <S.SpacedCaption>Electrical activity in the brain: EEG studies</S.SpacedCaption>
                <S.Text>
                  Electroencephalography measures fluctuations in electrical current at the scalp,
                  showing rhythmic brain activity (‘brainwaves’) and how this kind of activity
                  shifts in response to Brain.fm music.
                </S.Text>
              </S.SpacedTextWrapper>
              <S.ImageWrapper>
                <Image
                  alt={Assets.images.scienceEeg.alt}
                  height={712}
                  src={Assets.images.scienceEeg.url}
                  style={{ height: '440px', width: '100%', objectFit: 'contain' }}
                  width={1555}
                />
              </S.ImageWrapper>
            </S.SpacedWrapper>
            <S.SpacedWrapper>
              <S.SpacedTextWrapper>
                <S.SpacedCaption>Blood flow in the brain: FMRI studies</S.SpacedCaption>
                <S.Text>
                  Functional magnetic resonance imaging measures blood flow in the brain, showing
                  which brain regions and functional networks are more active in response to
                  Brain.fm versus other music.
                </S.Text>
              </S.SpacedTextWrapper>
              <S.ImageWrapper>
                <Image
                  alt={Assets.images.scienceFmri.alt}
                  height={433}
                  src={Assets.images.scienceFmri.url}
                  style={{ height: 'auto', width: '100%' }}
                  width={1470}
                />
              </S.ImageWrapper>
            </S.SpacedWrapper>
          </S.Content>
        </S.Section>
      </S.PanelWrapper>
      <S.Section>
        <S.Content>
          <S.SectionTextContainer>
            <S.SubCaption>What makes Brain.fm different?</S.SubCaption>
            <S.Caption>Patented neural entrainment</S.Caption>
            <S.Text>
              Brain.fm holds patents on technology to elicit strong neural phase locking—allowing
              populations of neurons to engage in various kinds of coordinated activity. This makes
              our music unique, purpose-built to steer you into a desired mental state.
            </S.Text>
            <S.CtaWrapper>
              <PrimaryButtonLink
                analyticsEvent={{
                  category: 'Purple CTA',
                  action: 'Science Page: CTA Button Click',
                }}
                variant="dark"
                onClick={handleClickCta}
              >
                Experience entrainment
              </PrimaryButtonLink>
            </S.CtaWrapper>
          </S.SectionTextContainer>
          <S.ImageWrapper>
            <Image
              alt={Assets.images.sciencePatented.alt}
              height={599}
              src={Assets.images.sciencePatented.url}
              style={{ height: 'auto', width: '100%' }}
              width={1499}
            />
          </S.ImageWrapper>
          <S.StatementsWrapper>
            <S.Statement>
              <div>
                <Image
                  alt={Assets.images.scienceIconNoDistraction.alt}
                  height={48}
                  src={Assets.images.scienceIconNoDistraction.url}
                  width={48}
                />
              </div>
              <S.StatementCaption>Music without distractions</S.StatementCaption>
              <S.StatementDescription>
                Most music in the world is designed to grab your attention, which leads to
                distraction. Instead, our process ensures that attention-grabbing elements are
                subdued or removed, allowing the music to sit comfortably in the background.
              </S.StatementDescription>
            </S.Statement>
            <S.Statement>
              <div>
                <Image
                  alt={Assets.images.scienceIconBeats.alt}
                  height={48}
                  src={Assets.images.scienceIconBeats.url}
                  width={48}
                />
              </div>
              <S.StatementCaption>Brain.fm is not ‘binaural beats’!</S.StatementCaption>
              <S.StatementDescription>
                Binaural beats produce weak neural synchrony while modulation directly in each
                stereo channel (used by Brain.fm) has much stronger effects on brain activity.
                Importantly, Brain.fm uses many methods to make our music work, unlike binaural
                beats which is just one technique!
              </S.StatementDescription>
            </S.Statement>
          </S.StatementsWrapper>
        </S.Content>
      </S.Section>
      <S.Section>
        <S.Content>
          <S.SectionTextContainer>
            <S.SubCaption>How is Brain.fm music made?</S.SubCaption>
            <S.Caption>
              Made with science, <br /> tested with science.
            </S.Caption>
            <S.Text>
              We use neuroscience to inform our music-making, but also use science to test the
              resulting music. These are two different roles of science at Brain.fm, setting us
              apart from any other music service.
            </S.Text>
            <S.CtaWrapper>
              <PrimaryButtonLink
                analyticsEvent={{
                  category: 'Purple CTA',
                  action: 'Science Page: CTA Button Click',
                }}
                href={WHITE_PAPER_LINK}
                variant="dark"
              >
                Read our white paper
              </PrimaryButtonLink>
            </S.CtaWrapper>
          </S.SectionTextContainer>
          <S.ImageWrapper>
            <Image
              alt={Assets.images.scienceTested.alt}
              height={558}
              src={Assets.images.scienceTested.url}
              style={{ height: 'auto', width: '100%' }}
              width={1470}
            />
          </S.ImageWrapper>
          <S.StatementsWrapper>
            <S.Statement>
              <div>
                <Image
                  alt={Assets.images.scienceIconPrecision.alt}
                  height={48}
                  src={Assets.images.scienceIconPrecision.url}
                  width={48}
                />
              </div>
              <S.StatementCaption>Human warmth meets scientific precision</S.StatementCaption>
              <S.StatementDescription>
                Music made purely by AI is often cold and boring, while human music targets your
                emotions, but lacks the precise features needed to affect your brain directly.
                Brain.fm gives you the best of both worlds by adding a layer of science to
                human-composed music.
              </S.StatementDescription>
            </S.Statement>
            <S.Statement>
              <div>
                <Image
                  alt={Assets.images.scienceIconBrain.alt}
                  height={48}
                  src={Assets.images.scienceIconBrain.url}
                  width={64}
                />
              </div>
              <S.StatementCaption>
                Brain.fm sounds different and affects you differently
              </S.StatementCaption>
              <S.StatementDescription>
                Our science-based process results in music with a unique sound that has unique
                effects on your brain and performance, unlike services that just curate pre-existing
                music that wasn’t originally meant for that purpose.
              </S.StatementDescription>
            </S.Statement>
          </S.StatementsWrapper>
        </S.Content>
      </S.Section>
    </S.Wrapper>
  );
};
